import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import {
  LinkCellRenderer,
  ManualIconCellRenderer,
} from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import { CASProcessByShift } from "../../models/ProcessByShift";

type Props = {
  showCommentModal: boolean;
  setShowCommentModal: (
    show: boolean,
    data: CASProcessByShift,
    position?: { top: number; left: number },
  ) => void;
};

export const useCASTableData = ({ setShowCommentModal }: Props) => {
  const columns: ColDef[] = [
    {
      field: "HeatName",
      headerName: "Heat",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/meltshop-heats/${params.data.HeatShiftId}?heatName=${params.data.HeatName}`,
        });
      },
      flex: 1,
      minWidth: 60,
    },
    {
      field: "GradeName",
      headerName: "Grade",
      flex: 1,
      minWidth: 80,
    },
    {
      field: "LadleName",
      headerName: "Ladle",
      flex: 1,
      minWidth: 60,
    },
    {
      field: "Crew",
      headerName: "Crew",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "Start",
      headerName: "Arrive",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
      flex: 1,
      minWidth: 60,
    },
    {
      field: "LadleOpen",
      headerName: "Ladle Open",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/caster-data-entry/${params.data.ProcessId}`,
        });
      },
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.LadleOpen, "HH:mm:ss") || "",
      flex: 1,
      minWidth: 80,
    },
    {
      field: "LadleClose",
      headerName: "Ladle Close",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.LadleClose, "HH:mm:ss") || "",
      flex: 1,
      minWidth: 85,
    },
    {
      field: "End",
      headerName: "End",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
      flex: 1,
      minWidth: 85,
    },
    {
      field: "Duration",
      headerName: "Duration",
      flex: 1,
      minWidth: 70,
      valueGetter: (params) => params.data?.Duration?.toFixed(1) || "",
    },

    {
      field: "MoldSize",
      headerName: "Size",
      flex: 1,
      minWidth: 60,
    },
    {
      field: "CastTons",
      headerName: "Tons",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 60,
      valueGetter: (params) => params.data?.CastTons?.toFixed(1) || "0",
    },
    {
      field: "LiquidusTemp",
      headerName: "Liquidus",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 75,
      valueGetter: (params) => params.data?.LiquidusTemp?.toFixed(0) || "",
    },
    {
      field: "FirstTemperature",
      headerName: "First Temp",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 75,
      valueGetter: (params) => params.data?.FirstTemperature?.toFixed(0) || "",
    },
    {
      field: "LastTemperature",
      headerName: "Last Temp",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 75,
      valueGetter: (params) => params.data?.LastTemperature?.toFixed(0) || "",
    },
    {
      field: "MinSuperHeat",
      headerName: "Min SH",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.MinSuperHeat?.toFixed(0) || "",
      flex: 1,
      minWidth: 55,
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      minWidth: 60,
      cellRenderer: (params: ICellRendererParams) => {
        return CommentsColumn({
          data: params?.data,
          setShowOpenModal: setShowCommentModal,
        });
      },
      valueGetter: (params) => params.data?.Comments || "",
    },
    {
      field: "IsManual",
      headerName: "",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: ManualIconCellRenderer,
      cellClass: "ag-button-cell",
      flex: 1,
      minWidth: 60,
    },
  ];

  return { columns };
};
